/**
 * objectPool.ts
 * Generic fixed-capacity object pool for the Starfall Courier game loop.
 *
 * Prevents GC pressure from repeated allocations of short-lived objects
 * (projectiles, particles, hit effects). Objects are recycled by returning
 * them to the pool rather than letting them be garbage-collected.
 *
 * Phase 0 stub — fully functional. Used by src/game/entities/ (Phase 1+).
 * See ROADMAP.md Phase 0.
 *
 * @module objectPool
 */

/** Hard cap on pool size, as required by AGENTS.md (max 50 active pooled objects). */
export const MAX_POOL_SIZE = 50;

/**
 * A simple fixed-capacity object pool.
 *
 * @template T The pooled object type. Must implement a `reset()` method so
 *   the pool can return the object to a clean state before re-use.
 */
export class ObjectPool<T extends { reset(): void }> {
  private readonly pool: T[] = [];
  private readonly factory: () => T;
  private readonly capacity: number;
  /** Running count of objects ever created by the factory. Never exceeds `capacity`. */
  private totalCreated = 0;

  /**
   * Create a new pool.
   *
   * `factory` is used **only** by {@link prewarm} to fill the pool at startup.
   * {@link acquire} never calls `factory` — when the pool is empty it returns
   * `null` and the caller is responsible for handling the miss. This keeps the
   * pool strictly fixed-capacity and prevents unbounded allocation in the hot path.
   *
   * @param factory - Constructs a fresh instance of T; called only during prewarm.
   * @param capacity - Max objects to hold in reserve. Must be 1–{@link MAX_POOL_SIZE}.
   * @throws {RangeError} If capacity is outside the valid range.
   */
  constructor(factory: () => T, capacity: number = MAX_POOL_SIZE) {
    if (capacity < 1 || capacity > MAX_POOL_SIZE) {
      throw new RangeError(
        `ObjectPool capacity must be between 1 and ${MAX_POOL_SIZE}. Got: ${capacity}`,
      );
    }
    this.factory = factory;
    this.capacity = capacity;
  }

  /**
   * Pre-warm the pool by eagerly creating up to `count` instances.
   * Call during scene initialisation to avoid first-frame hitching.
   * Respects the lifetime factory budget: factory is never called more than
   * `capacity` times across the pool's entire lifetime, even if `prewarm()` is
   * called again after objects have been acquired and the pool appears empty.
   * @param count - Number of instances to create. Clamped to remaining budget.
   */
  prewarm(count: number): void {
    const target = Math.min(count, this.capacity);
    // How many objects the pool still needs to reach the target level.
    const needed = Math.max(0, target - this.pool.length);
    // Remaining factory budget across this pool's lifetime.
    const budgetLeft = this.capacity - this.totalCreated;
    const toCreate = Math.min(needed, budgetLeft);
    for (let i = 0; i < toCreate; i++) {
      this.pool.push(this.factory());
      this.totalCreated++;
    }
  }

  /**
   * Acquire an object from the pool.
   * Returns `null` when the pool is empty — the caller must handle this case
   * (e.g. skip spawning the projectile) to prevent uncapped allocation.
   * @returns A recycled object ready for initialisation, or `null` if pool is empty.
   */
  acquire(): T | null {
    return this.pool.length > 0 ? (this.pool.pop() ?? null) : null;
  }

  /**
   * Return an object to the pool. Calls `reset()` before storing.
   * Silently drops the object if the pool is already at capacity.
   * @param obj - The object to recycle.
   */
  release(obj: T): void {
    if (this.pool.length < this.capacity) {
      obj.reset();
      this.pool.push(obj);
    }
  }

  /** Number of objects currently available for {@link acquire}. */
  get available(): number {
    return this.pool.length;
  }

  /** Total number of objects ever created by the factory (never exceeds capacity). */
  get totalAllocated(): number {
    return this.totalCreated;
  }

  /** The maximum number of objects this pool can hold. */
  get maxCapacity(): number {
    return this.capacity;
  }
}
